import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import apiConfig from '../config/apiConfig';
import './RightSidebar.css';

const RightSidebar = () => {
  const [activePlayers, setActivePlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [serverTime, setServerTime] = useState(new Date());
  
  // Use useRef for values that need to persist between renders without triggering rerenders
  const lastHashRef = useRef(null);

  // Update server time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setServerTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // Fetch active players with long polling support
  const fetchActivePlayers = useCallback(async (useHash = false) => {
    try {
      if (!useHash) setLoading(true);
      
      // For long polling we need to POST the hash in the request body
      const url = apiConfig.activeCharacters;
      
      console.log(`Fetching active players from: ${url}${useHash ? ' with long polling' : ''}`);
      
      const fetchOptions = {
        method: useHash ? 'POST' : 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      };
      
      // Add request body with hash for long polling
      if (useHash && lastHashRef.current !== null) {
        fetchOptions.body = JSON.stringify({ hash: lastHashRef.current });
      }
      
      const response = await fetch(url, fetchOptions);

      // Handle HTTP 304 (Not Modified) for long polling
      if (response.status === 304) {
        console.log('No changes to active players (304 Not Modified)');
        return false; // No changes
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch active players: ${response.status}`);
      }

      const data = await response.json();
      console.log('Active players data received:', data);
      
      // Extract hash and characters array
      if (data) {
        // Store the hash for future long polling requests
        if (data.hash !== undefined) {
          lastHashRef.current = data.hash;
        }
        
        // Extract and set characters array
        if (Array.isArray(data.characters)) {
          setActivePlayers(data.characters);
        } else if (Array.isArray(data)) {
          setActivePlayers(data);
        } else {
          console.warn('Unexpected data format from active players API:', data);
          setActivePlayers([]);
        }
        
        setLastUpdateTime(new Date());
      }
      
      return true; // Data changed
    } catch (err) {
      console.error('Error fetching active players:', err);
      setError('Failed to load active players');
      return false; // Error occurred
    } finally {
      if (!useHash) setLoading(false);
    }
  }, []); // Remove dependency on lastHash as we use a ref now

  // Initial fetch and long polling setup
  useEffect(() => {
    let isMounted = true;
    let pollingTimeout;
    
    // Long polling function
    const startLongPolling = () => {
      // Clear any existing timeout
      if (pollingTimeout) clearTimeout(pollingTimeout);
      
      // Set a timeout to prevent continuous polling if something goes wrong
      pollingTimeout = setTimeout(async () => {
        if (!isMounted) return;
        
        try {
          // Make the long polling request with hash
          const hasChanges = await fetchActivePlayers(true);
          
          // If we received changes or had an error, wait a bit before polling again
          if (isMounted) {
            setTimeout(() => {
              startLongPolling();
            }, hasChanges ? 5000 : 1000);
          }
        } catch (error) {
          // If something goes wrong, wait longer before trying again
          console.error('Long polling error:', error);
          if (isMounted) {
            setTimeout(() => {
              startLongPolling();
            }, 10000);
          }
        }
      }, 500);
    };
    
    // First fetch without long polling to get initial data and hash
    fetchActivePlayers(false).then(() => {
      if (isMounted) {
        // Set up long polling after getting initial data
        startLongPolling();
      }
    });
    
    // Clean up
    return () => {
      isMounted = false;
      if (pollingTimeout) clearTimeout(pollingTimeout);
    };
  }, [fetchActivePlayers]);

  // Format UTC time
  const formatUTCTime = (date) => {
    return date.toISOString().replace('T', ' ').substring(0, 19) + ' UTC';
  };

  return (
    <div className="right-sidebar">
      <div className="active-players-container sidebar-section">
        <h4>Active Players</h4>
        
        <div className="server-time">{formatUTCTime(serverTime)}</div>
        
        {loading && activePlayers.length === 0 && (
          <div className="loading-players">Loading...</div>
        )}
        
        {error && (
          <div className="error-message">{error}</div>
        )}
        
        {!loading && activePlayers.length === 0 && !error && (
          <div className="no-players">No players online</div>
        )}
        
        {activePlayers.length > 0 && (
          <>
            <ul className="players-list">
              {activePlayers.map(player => (
                <li key={player.id} className="player-item">
                  <Link to={`/character/view/${player.id}`} className="player-link">
                    <span className="player-name">
                      {player.username} 
                      {player.level !== undefined && 
                        <span className="player-level">({player.level})</span>
                      }
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            
            <div className="online-count">
              {activePlayers.length} player{activePlayers.length !== 1 ? 's' : ''} online
            </div>
          </>
        )}
        
        <div className="last-update">
          Last updated: {lastUpdateTime.toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
};

export default RightSidebar;
